import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useTechSession from '../../hooks/useTechSession';
import useLanguage from '../../hooks/useLanguage';
import useVoice from '../../hooks/useVoice';
import TechButton from '../../components/tech/TechButton';
import VoiceButton from '../../components/tech/VoiceButton';
import SafetyAlert from '../../components/tech/SafetyAlert';
import { startTechDay, seedDemoTasks } from '../../services/api';

/**
 * Start Day screen — calls backend, shows morning briefing, voice greeting.
 */
export default function StartDay() {
  const navigate = useNavigate();
  const { session, setWorkOrder } = useTechSession();
  const { language, t, isRTL } = useLanguage();
  const { speak, speaking } = useVoice();

  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [safetyAlert, setSafetyAlert] = useState(null);

  useEffect(() => {
    if (!session) return;

    startTechDay({
      technician_id: session.technicianId,
      project_id: session.projectId,
      language: session.language || language,
    })
      .then(data => {
        // If no tasks found from real data, seed demo tasks
        if (data.total_tasks === 0) {
          console.log('[StartDay] No tasks from DB, seeding demo tasks...');
          return seedDemoTasks(
            session.technicianId,
            data.project || 'Demo Project',
            session.language || language,
          );
        }
        return data;
      })
      .catch(err => {
        // If start-day fails (e.g. project not in DB), fallback to demo
        console.log('[StartDay] Real start-day failed, seeding demo:', err.message);
        return seedDemoTasks(
          session.technicianId,
          session.projectName || 'Demo Project',
          session.language || language,
        );
      })
      .then(data => {
        setBriefing(data);
        setWorkOrder(data.work_order_id);

        // Check for safety alerts
        if (data.weather_alerts?.length > 0) {
          setSafetyAlert({
            voice: data.weather_alerts[0],
            action: 'caution',
          });
        }

        // Auto-play voice greeting
        if (data.voice_greeting?.text) {
          setTimeout(() => {
            speak(data.voice_greeting.text, data.voice_greeting.language, data.voice_greeting.speed);
          }, 500);
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  if (!session) {
    navigate('/tech');
    return null;
  }

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner}>{t.loading}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.center}>
        <div style={{ color: '#d63031', marginBottom: 16 }}>{error}</div>
        <TechButton onClick={() => window.location.reload()}>{t.retry}</TechButton>
      </div>
    );
  }

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <SafetyAlert alert={safetyAlert} onDismiss={() => setSafetyAlert(null)} t={t} />

      {/* Greeting header */}
      <div style={styles.greetingBox}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={styles.greeting}>
            {t.goodMorning}, {session.fullName?.split(' ')[0]}!
          </h1>
          <VoiceButton
            mode="speak"
            active={speaking}
            onClick={() => {
              if (briefing?.voice_greeting?.text) {
                speak(briefing.voice_greeting.text, briefing.voice_greeting.language);
              }
            }}
          />
        </div>
        <div style={styles.project}>{briefing?.project}</div>
        <div style={styles.date}>{new Date().toLocaleDateString()}</div>
      </div>

      {/* Stats cards */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{briefing?.total_tasks || 0}</div>
          <div style={styles.statLabel}>{t.tasksToday}</div>
        </div>
        <div style={{ ...styles.statCard, borderColor: '#d63031' }}>
          <div style={{ ...styles.statValue, color: '#d63031' }}>{briefing?.critical_tasks || 0}</div>
          <div style={styles.statLabel}>{t.critical}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{briefing?.estimated_hours || 0}h</div>
          <div style={styles.statLabel}>{t.hours}</div>
        </div>
      </div>

      {/* Sites & travel summary */}
      {briefing?.sites_count > 1 && (
        <div style={styles.travelBanner}>
          <span>📍 {briefing.sites_count} {isRTL ? 'אתרים' : 'sites'}</span>
          <span style={styles.travelDot}>•</span>
          <span>🚗 {briefing.travel_hours}h {isRTL ? 'נסיעה' : 'travel'}</span>
          <span style={styles.travelDot}>•</span>
          <span>{briefing.sites?.join(' → ')}</span>
        </div>
      )}

      {/* Equipment grouped by site */}
      {briefing?.equipment_by_site?.length > 0 ? (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{t.equipment}</h3>
          {briefing.equipment_by_site.map((site, si) => (
            <div key={si} style={{ marginBottom: si < briefing.equipment_by_site.length - 1 ? 12 : 0 }}>
              <div style={styles.siteEquipHeader}>
                <span style={{ fontSize: 14 }}>📍</span>
                <span style={styles.siteEquipName}>{site.site}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                {site.parts.map((item, i) => (
                  <div key={i} style={styles.equipRow}>
                    <span style={{ fontSize: 13, color: '#b1bac4' }}>{item.name}</span>
                    <span style={styles.qtyBadge}>x{item.qty}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : briefing?.equipment_list?.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{t.equipment}</h3>
          {briefing.equipment_with_qty?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {briefing.equipment_with_qty.map((item, i) => (
                <div key={i} style={styles.equipRow}>
                  <span style={{ fontSize: 13, color: '#b1bac4' }}>{item.name}</span>
                  <span style={styles.qtyBadge}>x{item.qty}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.chipList}>
              {briefing.equipment_list.map((item, i) => (
                <span key={i} style={styles.chip}>{item}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Removed tasks notice */}
      {briefing?.removed_tasks?.length > 0 && (
        <div style={styles.removedSection}>
          <h3 style={{ ...styles.sectionTitle, color: '#fdcb6e' }}>
            ⚠️ {isRTL ? 'משימות שנדחו (יום ארוך מדי)' : 'Deferred tasks (day too long)'}
          </h3>
          {briefing.removed_tasks.map((rt, i) => (
            <div key={i} style={styles.removedRow}>
              <span style={{ fontSize: 13, color: '#b1bac4' }}>
                {isRTL ? 'רובוט' : 'Robot'} {rt.robot_number} — {rt.project_name}
              </span>
              <span style={{
                fontSize: 11, color: rt.priority === 'CRITICAL' ? '#d63031' : '#fdcb6e',
                fontWeight: 600,
              }}>
                {rt.priority}
              </span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: '#8b949e', marginTop: 6 }}>
            {isRTL ? 'המשימות הוחזרו לפול הרובוטים הממתינים' : 'Tasks returned to pending robot pool'}
          </div>
        </div>
      )}

      {/* Safety reminders */}
      {briefing?.safety_reminders?.length > 0 && (
        <div style={styles.section}>
          <h3 style={{ ...styles.sectionTitle, color: '#fdcb6e' }}>{t.safety}</h3>
          {briefing.safety_reminders.map((r, i) => (
            <div key={i} style={styles.safetyItem}>{r}</div>
          ))}
        </div>
      )}

      {/* Go to tasks button */}
      <TechButton
        fullWidth
        onClick={() => navigate('/tech/tasks')}
        style={{ marginTop: 24 }}
      >
        {t.tasks} ({briefing?.total_tasks || 0}) {'\u2192'}
      </TechButton>
    </div>
  );
}

const styles = {
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
  },
  spinner: {
    color: '#8b949e',
    fontSize: 16,
  },
  greetingBox: {
    background: '#161b22',
    borderRadius: 16,
    padding: '20px',
    marginBottom: 16,
  },
  greeting: {
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
    color: '#e6edf3',
  },
  project: {
    fontSize: 14,
    color: '#6c5ce7',
    marginTop: 4,
    fontWeight: 600,
  },
  date: {
    fontSize: 12,
    color: '#8b949e',
    marginTop: 2,
  },
  statsRow: {
    display: 'flex',
    gap: 10,
    marginBottom: 16,
  },
  statCard: {
    flex: 1,
    background: '#161b22',
    borderRadius: 12,
    padding: '14px 12px',
    textAlign: 'center',
    border: '1px solid #30363d',
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#e6edf3',
  },
  statLabel: {
    fontSize: 11,
    color: '#8b949e',
    marginTop: 2,
  },
  section: {
    background: '#161b22',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#e6edf3',
    margin: '0 0 8px 0',
  },
  chipList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    fontSize: 12,
    padding: '4px 10px',
    background: '#30363d',
    borderRadius: 16,
    color: '#b1bac4',
  },
  safetyItem: {
    fontSize: 13,
    color: '#fdcb6e',
    padding: '4px 0',
    borderBottom: '1px solid #30363d20',
  },
  travelBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
    borderRadius: 10,
    marginBottom: 12,
    fontSize: 13,
    color: '#b1bac4',
    flexWrap: 'wrap',
  },
  travelDot: {
    color: '#30363d',
  },
  siteEquipHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 0',
    borderBottom: '1px solid #30363d',
    marginBottom: 2,
  },
  siteEquipName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#e6edf3',
  },
  equipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#0d1117',
    borderRadius: 8,
    border: '1px solid #30363d',
  },
  qtyBadge: {
    fontSize: 12,
    fontWeight: 700,
    color: '#6c5ce7',
    background: '#6c5ce720',
    padding: '2px 8px',
    borderRadius: 10,
  },
  removedSection: {
    background: '#161b22',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 12,
    borderLeft: '3px solid #fdcb6e',
  },
  removedRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 12px',
    background: '#0d1117',
    borderRadius: 8,
    border: '1px solid #30363d40',
    marginBottom: 4,
  },
};
